import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import dayjs from 'dayjs';
import { Button } from '@/design-system/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/design-system/dropdown-menu';
import { FormModal } from '@/design-system/form-modal';
import { IconButton } from '@/design-system/icon-button';
import { Paginator } from '@/design-system/pagination';
import { SearchField } from '@/design-system/search-field';
import { SearchSelect } from '@/design-system/search-select';
import { TableData, TableDataColumn } from '@/design-system/table-data';
import { toast } from '@/design-system/toaster';
import { useProvidersDropdown } from '@/pages/shipments/components/useProvidersDropdown';
import { useUrlParam, useUrlNumber, useUrlSearch } from '@/hooks/urlState';
import {
  getPackages,
  cancelPackage,
  PackageRow,
  PackageListParams,
  PACKAGE_STATUS_FILTERS,
  PackageStatus,
  PackageStatusFilter,
} from '@/services/api/packages';
import { getBackendErrorMessage } from '@/utils/errorHandler';
import EditPackageModal from './EditPackageModal';
import PackageDetailPanel from './PackageDetailPanel';
import {
  PackageConditionBadge,
  PackageStatusBadge,
  STATUS_LABELS,
} from './PackageBadges';

// Lazy so the react-pdf bundle is only fetched when a user opens the label
// modal, not on initial app load.
const LabelPreviewModal = lazy(() => import('@/components/LabelPreviewModal'));

const MAX_BULK_PRINT = 100;

const formatDateTime = (value?: string | null) =>
  value ? dayjs(value).format('DD MMM YYYY, HH:mm') : '—';

// Weight/volume stay editable through the hub-side lifecycle; a package can only
// be cancelled while still CREATED (matches the backend rules).
const EDITABLE_STATUSES: PackageStatus[] = ['CREATED', 'HUB_SCANNED'];
const CANCELLABLE_STATUSES: PackageStatus[] = ['CREATED'];

const PackagesPage: React.FC = () => {
  const [packages, setPackages] = useState<PackageRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // Filters / pagination are URL-driven so the view is shareable and the back
  // button works. Filter changes drop `page` in the same URL write (resetPage),
  // so a filter change and its page-reset are one write / one fetch.
  const [page, setPage] = useUrlNumber('page', 1, { min: 1 });
  const [pageSize, setPageSize] = useUrlNumber('pageSize', 20, {
    min: 1,
    resetPage: true,
  });
  // v2 filter values only — a legacy status would 400.
  const [statusFilter, setStatusFilter] = useUrlParam<PackageStatusFilter | ''>(
    'status',
    '',
    { allowed: PACKAGE_STATUS_FILTERS, resetPage: true }
  );
  // Optional client filter (BE lists across all clients when omitted).
  const [clientId, setClientId] = useUrlParam<string>('client', '', {
    resetPage: true,
  });

  // Search: URL is the source of truth; the box resyncs when the URL changes
  // externally (Back/forward, a programmatic jump). Debounced write.
  const {
    value: search,
    setValue: setSearch,
    committed: debouncedSearch,
  } = useUrlSearch('q', { resetPage: true, delay: 500 });

  const clients = useProvidersDropdown(true);

  // Keep the selected client's label visible even after a narrowing search
  // drops it out of the current page.
  const clientOptions = useMemo(() => {
    const opts = clients.options.map((p) => ({
      value: String(p.id),
      label: p.name,
    }));
    if (clientId && !opts.some((o) => o.value === clientId)) {
      const label = clients.labelFor(clientId);
      if (label) opts.unshift({ value: clientId, label });
    }
    return opts;
  }, [clients, clientId]);

  // Bulk-print selection, keyed by unique item id (record.id).
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [labelModalOpen, setLabelModalOpen] = useState(false);
  const [labelTargetIds, setLabelTargetIds] = useState<string[]>([]);

  // Manage (edit / cancel) state.
  const [editPkg, setEditPkg] = useState<PackageRow | null>(null);
  const [cancelTarget, setCancelTarget] = useState<PackageRow | null>(null);
  const [cancelling, setCancelling] = useState(false);

  // Row click opens the read-only detail drawer (package info + POD photos).
  const [detailPkg, setDetailPkg] = useState<PackageRow | null>(null);

  const fetchPackages = useCallback(async () => {
    setLoading(true);
    try {
      const params: PackageListParams = {
        clientId: clientId ? Number(clientId) : undefined,
        search: debouncedSearch || undefined,
        status: statusFilter || undefined,
        page,
        limit: pageSize,
      };
      const res = await getPackages(params);
      setPackages(res.packages ?? []);
      setTotal(res.total ?? 0);
    } catch (err) {
      toast.error('Failed to load packages', {
        description: getBackendErrorMessage(err),
      });
      setPackages([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [clientId, debouncedSearch, statusFilter, page, pageSize]);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  const openLabelModal = (ids: string[]) => {
    setLabelTargetIds(ids);
    setLabelModalOpen(true);
  };

  const handleCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await cancelPackage(cancelTarget.id);
      toast.success('Package cancelled', {
        description: `${cancelTarget.itemName || cancelTarget.po} has been cancelled.`,
      });
      setSelectedIds((prev) => prev.filter((id) => id !== cancelTarget.id));
      setCancelTarget(null);
      fetchPackages();
    } catch (err) {
      toast.error('Failed to cancel', {
        description: getBackendErrorMessage(err),
      });
    } finally {
      setCancelling(false);
    }
  };

  const printSelected = () => {
    if (selectedIds.length === 0) {
      toast.error('No POs selected', {
        description: 'Select at least one PO to print.',
      });
      return;
    }
    if (selectedIds.length > MAX_BULK_PRINT) {
      toast.error('Too many POs', {
        description: `Maximum ${MAX_BULK_PRINT} POs per bulk print. Select ${MAX_BULK_PRINT} or fewer.`,
      });
      return;
    }
    openLabelModal(selectedIds);
  };

  const columns: TableDataColumn<PackageRow>[] = [
    {
      key: 'po',
      title: 'Item Name / Invoice',
      render: (_v, record) => (
        <div>
          <div
            className="font-mono text-xs font-semibold text-accent"
            style={{ wordBreak: 'break-all' }}
          >
            {record.itemName}
          </div>
          {record.itemName && record.itemName !== record.po && (
            <div className="mt-0.5 text-xs text-text-soft-400">{record.po}</div>
          )}
        </div>
      ),
    },
    {
      key: 'createdAt',
      title: 'Created Date',
      render: (_v, record) => (
        <span className="text-xs text-text-sub-600">
          {formatDateTime(record.createdAt)}
        </span>
      ),
    },
    {
      key: 'bookingId',
      title: 'Booking ID',
      render: (_v, record) => (
        <span className="font-mono text-xs text-text-sub-600">
          {record.bookingId || '—'}
        </span>
      ),
    },
    {
      key: 'outlet',
      title: 'Recipient',
      render: (_v, record) => (
        <span className="text-sm font-medium text-text-strong-950">
          {record.outlet}
        </span>
      ),
    },
    {
      key: 'sender',
      title: 'Sender',
      render: (_v, record) => (
        <span className="text-sm text-text-sub-600">{record.sender}</span>
      ),
    },
    {
      key: 'client',
      title: 'Client',
      render: (_v, record) => (
        <span className="text-sm text-text-sub-600">
          {record.client?.name ?? '—'}
        </span>
      ),
    },
    {
      key: 'qty',
      title: 'Qty',
      align: 'right',
      render: (_v, record) => (
        <span className="text-sm tabular-nums text-text-strong-950">
          {record.qty}
        </span>
      ),
    },
    {
      // Physical parcel count — how many boxes this PO is. Distinct from `qty`
      // (units of goods), which is why it gets its own column.
      key: 'koli',
      title: 'Koli',
      align: 'right',
      render: (_v, record) => (
        <span className="text-sm tabular-nums text-text-strong-950">
          {record.koli ?? 1}
        </span>
      ),
    },
    {
      key: 'weight',
      title: 'Weight',
      align: 'right',
      render: (_v, record) => (
        <span className="text-sm tabular-nums text-text-sub-600">
          {record.weight} kg
        </span>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      // Condition sits beside the status: v2 moved damage off the status axis,
      // so a damaged parcel shows both where it is and that it's damaged.
      render: (_v, record) => (
        <div className="flex flex-wrap items-center gap-1">
          <PackageStatusBadge status={record.status} />
          <PackageConditionBadge condition={record.condition} />
        </div>
      ),
    },
    {
      key: 'actions',
      title: '',
      align: 'right',
      render: (_v, record) => {
        if (record.status === 'CANCELLED') return null;

        const canEdit = EDITABLE_STATUSES.includes(record.status);
        const canCancel = CANCELLABLE_STATUSES.includes(record.status);
        const hasMenu = canEdit || canCancel;

        return (
          <div
            className="flex items-center justify-end gap-1.5"
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              tone="neutral"
              style="stroke"
              size="xs"
              onClick={() => openLabelModal([record.id])}
            >
              <i className="ri-printer-line mr-1" />
              Print
            </Button>
            <div className="flex w-7 shrink-0 justify-center">
              {hasMenu && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <IconButton
                      tone="neutral"
                      style="ghost"
                      size="sm"
                      aria-label={`Manage ${record.po}`}
                    >
                      <i className="ri-more-2-fill text-[18px]" />
                    </IconButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {canEdit && (
                      <DropdownMenuItem onSelect={() => setEditPkg(record)}>
                        <i className="ri-edit-line" />
                        Edit weight &amp; volume
                      </DropdownMenuItem>
                    )}
                    {canCancel && (
                      <DropdownMenuItem
                        onSelect={() => setCancelTarget(record)}
                      >
                        <i className="ri-close-circle-line" />
                        Cancel package
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        );
      },
    },
  ];

  // Stamp each label with its owning client for the header ("DASH | {client}").
  // Prefer the per-package client from the backend; otherwise fall back to the
  // selected client filter (empty when printing across "All clients").
  const selectedClientName = clientId ? clients.labelFor(clientId) : '';
  const filterClient =
    clientId && selectedClientName
      ? { id: Number(clientId), name: selectedClientName }
      : undefined;
  const modalPackages = packages
    .filter((p) => labelTargetIds.includes(p.id))
    .map((p) => ({ ...p, client: p.client ?? filterClient }));

  return (
    <div className="space-y-5 p-6">
      {/* Page head */}
      <div className="flex flex-col gap-4 border-b border-stroke-soft-200 pb-5 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-[28px] font-extrabold tracking-tight text-text-strong-950">
            Packages
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-text-sub-600">
            Print a PO label for each package — stick it on the package so hub
            teams &amp; drivers can scan it via QR. Select multiple POs to
            bulk-print them in one document.
          </p>
        </div>
        <div className="flex flex-shrink-0 flex-wrap gap-2">
          <Button onClick={printSelected}>
            <i className="ri-printer-line mr-1.5" />
            {selectedIds.length > 0
              ? `Print labels (${selectedIds.length})`
              : 'Print labels'}
          </Button>
        </div>
      </div>

      {/* Table card */}
      <div className="rounded-lg border border-stroke-soft-200 bg-white">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stroke-soft-200 px-4 py-3">
          <SearchField
            value={search}
            onChange={setSearch}
            placeholder="Search PO, Booking ID, outlet…"
            size="lg"
            className="min-w-[220px] max-w-sm flex-1"
          />
          <div className="flex items-center gap-2">
            <div className="w-56">
              <SearchSelect
                options={clientOptions}
                value={clientId || undefined}
                onChange={(value) => setClientId((value as string) ?? '')}
                placeholder="All clients"
                emptyMessage="No clients found"
                allowClear
                loading={clients.loading}
                disableLocalFilter
                onSearchChange={clients.setSearch}
                onLoadMore={clients.loadMore}
              />
            </div>
            <div className="w-48">
              <SearchSelect
                options={PACKAGE_STATUS_FILTERS.map((s) => ({
                  value: s,
                  label: STATUS_LABELS[s] ?? s,
                }))}
                value={statusFilter || undefined}
                onChange={(v) =>
                  setStatusFilter((v as PackageStatusFilter | undefined) ?? '')
                }
                placeholder="All statuses"
                allowClear
                emptyMessage="No matching status"
              />
            </div>
          </div>
        </div>

        <div className="p-4">
          <TableData<PackageRow>
            data={packages}
            columns={columns}
            loading={loading}
            size="sm"
            selection={{
              selectedKeys: selectedIds,
              onSelectionChange: setSelectedIds,
              getRowKey: (r) => r.id,
              // Cancelled rows are inert — nothing to print.
              isRowSelectable: (r) => r.status !== 'CANCELLED',
            }}
            onRowClick={(record) => {
              if (record.status !== 'CANCELLED') setDetailPkg(record);
            }}
          />

          {total > 0 && (
            <Paginator
              currentPage={page}
              totalItems={total}
              itemsPerPage={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          )}
        </div>
      </div>

      {labelModalOpen && (
        <Suspense fallback={null}>
          <LabelPreviewModal
            open={labelModalOpen}
            onClose={() => setLabelModalOpen(false)}
            packages={modalPackages}
          />
        </Suspense>
      )}

      <PackageDetailPanel pkg={detailPkg} onClose={() => setDetailPkg(null)} />

      <EditPackageModal
        pkg={editPkg}
        open={editPkg !== null}
        onClose={() => setEditPkg(null)}
        onSaved={fetchPackages}
      />

      <FormModal
        open={cancelTarget !== null}
        onClose={() => (cancelling ? undefined : setCancelTarget(null))}
        icon={<i className="ri-close-circle-line" />}
        iconTone="danger"
        title="Cancel this package?"
        subtitle={cancelTarget?.itemName || cancelTarget?.po}
        size="sm"
        closeOnBackdropClick={!cancelling}
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button
              tone="neutral"
              style="stroke"
              onClick={() => setCancelTarget(null)}
              disabled={cancelling}
            >
              Keep package
            </Button>
            <Button
              tone="destructive"
              onClick={handleCancel}
              disabled={cancelling}
            >
              {cancelling && (
                <i className="ri-loader-4-line mr-1.5 animate-spin" />
              )}
              Cancel package
            </Button>
          </div>
        }
      >
        <p className="text-sm text-text-sub-600">
          This package will be marked <b>cancelled</b> and removed from the
          shipment total. This action cannot be undone.
        </p>
      </FormModal>
    </div>
  );
};

export default PackagesPage;
